import React from 'react';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { useMemo } from 'react';
import { useState } from 'react';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { DataSetPostProcessingScriptContents } from 'libs/end-to-end/src/types/databases/data-sets/data-set-post-processing-script-contents';
import { useUserUploadHeadless } from 'apps/sigma2-ui/src/users/lib/useUserUploadHeadless';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { CreateDataSetPostProcessingScriptSchema } from 'libs/end-to-end/src/types/databases/data-sets/create-data-set-post-processing-script-request-body';
import { DataSet } from 'libs/end-to-end/src/types/databases/data-sets/data-set';
import { useSimpleReadWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadWritePattern';
import { CreateOrUpdateDataSetSchema } from 'libs/end-to-end/src/types/databases/data-sets/create-or-update-data-set-request-body';

export interface DataSetPostProcessingScriptPageHook {
  code: string;
  setCode: Setter<string>;
  isLoadingScript: boolean;
  isError: boolean;
  backButtonBehavior: TouchTargetBehavior;
  backButtonDisabled: boolean;
  backButtonLabel: string;
  saveButtonDisabled: boolean;
  saveButtonBehavior: TouchTargetBehavior;
  saveButtonLabel: string;
  loadSamplePresetButtonDisabled: boolean;
  loadSamplePresetButtonBehavior: TouchTargetBehavior;
  loadSamplePresetButtonLabel: string;
  loadMergeToAIInterviewerPresetButtonDisabled: boolean;
  loadMergeToAIInterviewerPresetButtonBehavior: TouchTargetBehavior;
  loadMergeToAIInterviewerPresetButtonLabel: string;
  runOnBatch: boolean;
  setRunOnBatch: Setter<boolean>;
}

export const dataSetPostProcessingStartingCode = `import pandas as pd
import numpy as np
import s2utils

def run_post_processing(datasources, output_dir):
    ...start typing here...
`;

export const mergeToAIInterviewerPresetCode = `import s2utils

def run_post_processing(datasources, output_dir):
    s2utils.convert_merge_applications_to_ai_interviewer_sources(datasources, output_dir)
`;

export const useDataSetPostProcessingScriptPage = ({
  organizationId,
  databaseId,
  dataSetId,
}: {
  dataSetId: string;
  databaseId: string;
  organizationId: string;
}): DataSetPostProcessingScriptPageHook => {
  const { myUser } = useUser();
  const [code, setCode] = useState<string>('');
  const [runOnBatch, setRunOnBatch] = useState<boolean>(false);

  const getUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSetId}/post-processing-script`;
  }, [dataSetId, databaseId, organizationId]);

  const getDataSetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSetId}`;
  }, [dataSetId, databaseId, organizationId]);

  const readHook = useSimpleReadPattern({
    getUrlFn,
    canRead: true,
    responseSchema: DataSetPostProcessingScriptContents,
  });

  const dataSetHook = useSimpleReadWritePattern({
    getReadUrlFn: getDataSetUrlFn,
    getWriteUrlFn: getDataSetUrlFn,
    canWrite: true,
    canRead: true,
    writeSchema: CreateOrUpdateDataSetSchema,
    responseSchema: DataSet,
  });

  const writeHook = useSimpleWritePattern({
    getUrlFn,
    canWrite: true,
    writeSchema: CreateDataSetPostProcessingScriptSchema,
    method: 'POST',
    responseSchema: EmptyObject,
  });

  const userId = useMemo(() => {
    return myUser?.id ?? null;
  }, [myUser?.id]);

  const { upload, canUpload } = useUserUploadHeadless({
    userId,
  });

  // This is true when it is safe to save the new settings into the data set
  const canWriteDataSet = useMemo(
    () =>
      (dataSetHook.status === 'done' || dataSetHook.status === 'error') &&
      dataSetHook.entity !== null,
    [dataSetHook],
  );

  const saveCodeAndSettings = useCallback(() => {
    if (!canUpload || writeHook.status !== 'done' || !canWriteDataSet) {
      return;
    }

    upload(new Blob([code], { type: 'text/plain' }), (scriptUserUploadId) => {
      if (!('saveEntity' in dataSetHook) || dataSetHook.entity === null) {
        return;
      }

      dataSetHook.saveEntity({
        ...dataSetHook.entity,
        postProcessingSettings: {
          ...dataSetHook.entity.postProcessingSettings,
          runOnBatch,
        },
      });

      writeHook.saveEntity({
        scriptUserUploadId,
      });
    });
  }, [
    canUpload,
    writeHook,
    canWriteDataSet,
    upload,
    code,
    dataSetHook,
    runOnBatch,
  ]);

  // Once the script loads, update the code
  useEffect(() => {
    if (readHook.status === 'done') {
      setCode(readHook.entity.contents);
    }
  }, [readHook]);

  // Once the data set loads, update the runOnBatch setting
  useEffect(() => {
    if (dataSetHook.status === 'done') {
      setRunOnBatch(
        dataSetHook.entity.postProcessingSettings?.runOnBatch ?? false,
      );
    }
  }, [dataSetHook]);

  return useMemo(() => {
    const isLoadingScript = readHook.status === 'loading';
    const isSavingScript = writeHook.status === 'saving';
    const isLoadingDataSet = dataSetHook.status === 'loading';
    const isSavingDataSet = dataSetHook.status === 'saving';
    const isError =
      readHook.status === 'error' ||
      writeHook.status === 'error' ||
      dataSetHook.status === 'error';

    const isUIDisabled =
      isLoadingScript ||
      isError ||
      isSavingScript ||
      isSavingDataSet ||
      isLoadingDataSet;

    return {
      code,
      setCode,
      isLoadingScript,
      isError,
      backButtonBehavior: {
        type: 'href',
        href: `/organizations/${organizationId}/databases/${databaseId}/data-sets`,
      },
      backButtonDisabled: isUIDisabled,
      backButtonLabel: 'Back',
      saveButtonDisabled: isUIDisabled || !canUpload || !canWriteDataSet,
      saveButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          saveCodeAndSettings();
        },
      },
      saveButtonLabel: 'Save',
      loadSamplePresetButtonDisabled: isUIDisabled,
      loadSamplePresetButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          setCode(dataSetPostProcessingStartingCode);
        },
      },
      loadSamplePresetButtonLabel: 'Load Preset',
      loadMergeToAIInterviewerPresetButtonDisabled: isUIDisabled,
      loadMergeToAIInterviewerPresetButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          setCode(mergeToAIInterviewerPresetCode);
        },
      },
      loadMergeToAIInterviewerPresetButtonLabel:
        'Load Merge to AI Interviewer Preset',
      runOnBatch,
      setRunOnBatch,
    };
  }, [
    canUpload,
    canWriteDataSet,
    code,
    dataSetHook.status,
    databaseId,
    organizationId,
    readHook.status,
    runOnBatch,
    saveCodeAndSettings,
    writeHook.status,
  ]);
};
